import { SyntheticEvent, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useShell } from '@/providers';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';
import { useRegistryRoutes } from '@/hooks/useRegistryRoutes';
import {
  AdjustmentsVerticalIcon,
  AnimalsIcon,
  GroupIcon,
  LeadingIcon,
} from '@/components/icons';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { navigationMessages } from './messages';
import ShellNavItems from './ShellNavItems';
import { Box } from '@/primitives';

export default function ShellSidebarLinks() {
  const pathname = usePathname();
  const { isBreedlogicAdmin } = useCurrentOrg();
  const canManageUserAssignments = isBreedlogicAdmin;
  const canRunSolrSync = isBreedlogicAdmin;
  const registryRoutes = useRegistryRoutes();
  const { configMenuOpen, sidebarExpanded, setConfigMenuOpen } = useShell();

  const navItems = useMemo(
    () => [
      {
        href: registryRoutes.MEMBERS,
        label: navigationMessages['navigation.menuEntry.members.label'],
        selected: pathname.startsWith(registryRoutes.MEMBERS),
        startIcon: <LeadingIcon />,
      },
      {
        href: registryRoutes.ANIMALS,
        label: navigationMessages['navigation.menuEntry.animals.label'],
        selected: pathname.startsWith(registryRoutes.ANIMALS),
        startIcon: <AnimalsIcon />,
      },
      {
        href: registryRoutes.IMPORTS,
        label: navigationMessages['navigation.menuEntry.imports.label'],
        selected: pathname.startsWith(registryRoutes.IMPORTS),
        startIcon: <UploadFileIcon />,
      },
      ...(canManageUserAssignments
        ? [
            {
              href: registryRoutes.USER_ASSIGNMENTS,
              label:
                navigationMessages[
                  'navigation.menuEntry.userAssignments.label'
                ],
              selected: pathname.startsWith(registryRoutes.USER_ASSIGNMENTS),
              startIcon: <GroupIcon />,
            },
          ]
        : []),
      ...(canRunSolrSync
        ? [
            {
              href: registryRoutes.SOLR_SYNC,
              label: navigationMessages['navigation.menuEntry.solrSync.label'],
              selected: pathname.startsWith(registryRoutes.SOLR_SYNC),
              startIcon: <SyncIcon />,
            },
          ]
        : []),
      {
        expanded: configMenuOpen,
        href: '',
        label: navigationMessages['navigation.menuEntry.configurations.label'],
        selected:
          pathname.startsWith(registryRoutes.FIELDS_CONFIG) ||
          pathname.startsWith(registryRoutes.FORMS_MANANGER) ||
          pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES) ||
          pathname.startsWith(registryRoutes.RULES),
        startIcon: <AdjustmentsVerticalIcon />,
        endIcon: sidebarExpanded ? (
          <Box>{configMenuOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}</Box>
        ) : undefined,
        subnavs: [
          {
            href: registryRoutes.FIELDS_CONFIG,
            label:
              navigationMessages[
                'navigation.menuEntry.form-builder.fields-config.label'
              ],
            selected: pathname.startsWith(registryRoutes.FIELDS_CONFIG),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.FORMS_MANANGER,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.formsManager.label'
              ],
            selected: pathname.startsWith(registryRoutes.FORMS_MANANGER),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.CERTIFICATE_TEMPLATES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.certificateTemplates.label'
              ],
            selected: pathname.startsWith(registryRoutes.CERTIFICATE_TEMPLATES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
          {
            href: registryRoutes.RULES,
            label:
              navigationMessages[
                'navigation.subMenu.configurations.rules.label'
              ],
            selected: pathname.startsWith(registryRoutes.RULES),
            onClick: () => !sidebarExpanded && setConfigMenuOpen(false),
          },
        ],
        onClick: (event: SyntheticEvent) => {
          event.preventDefault();
          setConfigMenuOpen(!configMenuOpen);
        },
      },
    ],
    [
      canManageUserAssignments,
      canRunSolrSync,
      configMenuOpen,
      pathname,
      registryRoutes,
      sidebarExpanded,
      setConfigMenuOpen,
    ],
  );

  return <ShellNavItems items={navItems} />;
}
